import React, {forwardRef, useRef} from 'react';
import {
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingPortal,
    useTransitionStatus,
} from '@floating-ui/react';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {useZIndexStore} from './hooks/use-z-index-store';
import styles from './floating.component.module.styl';

import type {ComponentProps, ReactNode} from 'react';
import type {FloatingContext} from '@floating-ui/react';
import {useReturnFocusState} from './hooks/use-return-focus-state';


const classNames = withCssModule(styles);

type NativeProps = Omit<ComponentProps<'div'>, 'ref'>;

export interface FloatingPopoverProps extends NativeProps {
    children: ReactNode,
    open?: boolean,
    listItemsRef: React.MutableRefObject<(HTMLElement | null)[]>,
    labelsRef?: React.MutableRefObject<(string | null)[]>,
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    getContext: () => FloatingContext,
    portalProps?: ComponentProps<typeof FloatingPortal>,
    floatingFocusManagerProps?: Partial<ComponentProps<typeof FloatingFocusManager>>,
    /**
     * If popover style should be flipped
     * */
    flip?: boolean,
    nodeId: string,
}

/**
 * A basic component for creating floating elements with Floating UI. Use it with useFloating hook.
 *
 * Renders a floating non-menu popover.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-floating--documentation}
 * */
export const FloatingPopover = forwardRef<HTMLDivElement, FloatingPopoverProps>(({
    children,
    open,
    listItemsRef,
    labelsRef,
    getContext,
    portalProps,
    floatingFocusManagerProps,
    flip,
    nodeId,
    ...nativeProps
}, ref) => {
    const context = getContext();

    // do not update children when closing with transition
    const childrenRef = useRef(children);
    if (context.open) {
        childrenRef.current = children;
    }

    const {isMounted, status} = useTransitionStatus(context, {
        duration: 150,
    });

    const {zIndex} = useZIndexStore(nodeId, context.open);

    const floatingClasses = classNames(nativeProps.className, 'floating', status, {flip});

    const returnFocus = useReturnFocusState(context);

    return (isMounted || open) && childrenRef.current ? (
        <FloatingNode id={nodeId}>
            <FloatingList elementsRef={listItemsRef} labelsRef={labelsRef}>
                <FloatingPortal {...portalProps}>
                    <FloatingFocusManager
                        returnFocus={returnFocus}
                        disabled={status !== 'open'}
                        {...floatingFocusManagerProps}
                        context={context}>
                        <div
                            ref={ref}
                            {...nativeProps}
                            style={{
                                zIndex,
                                ...nativeProps.style,
                            }}
                            className={floatingClasses}>
                            {childrenRef.current}
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            </FloatingList>
        </FloatingNode>
    ) : null;
});

FloatingPopover.displayName = 'FloatingPopover';
